// Tuning values. Keeping the magic numbers in one place makes the game far
// easier to balance without hunting through the render and physics modules.

/** Rounds a player must win to take the match. */
/** How long a match is unless the setup says otherwise. Three, as it always was. */
export const TARGET_WINS_DEFAULT = 3;
/**
 * The lengths a match can be, and the one that is not a length at all: `0` is a match with no end
 * to it, which is what the last button of that row is.
 *
 * The order is not only for the buttons: the invitation packs the choice into its mixed letter by
 * index (see matchcode.js), so an invitation written before this choice existed reads a length
 * that is not the one that went out. The mode and the clock still read right — they are the lower
 * digits of the same number and this is the higher one — which is the part that would break a match
 * between two machines.
 */
export const TARGET_WINS_CHOICES = [1, 3, 5, 7, 0];

/** Pause after a round-winning hit, long enough for the blast to play out. */
export const ROUND_DELAY_MS = 1800;

// --- the field -------------------------------------------------------------
/**
 * Lo ancho que mide el terreno en disputa, dentro de un mundo que es más grande.
 *
 * El lienzo mide 1280 y el campo no: todo lo que hay en este juego —el lanzamiento, la
 * gravedad, el viento, la puntería de la CPU— está ajustado para los 960 con los que nació, y
 * las perchas y la caja que cae se reparten dentro de esta banda, centrada, mientras lo que
 * queda fuera de ella es ciudad que mirar y no suelo por el que pelearse.
 *
 * Son 1080 y no 960 desde que el mundo creció: la distancia entre los dos gorilas la fija este
 * ancho, y con 960 quedaban más juntos de lo que pide el cuadro nuevo. Un tiro a 45° y toda la
 * fuerza llega a unos 1800 píxeles, así que 120 más no comprometen el alcance ni la puntería
 * de la CPU, que simula sus tiros con esta misma física.
 */
export const FIELD_W = 1080;

// --- projectile physics -----------------------------------------------------
/** Downward acceleration applied every frame. */
export const GRAVITY = 0.072;
/** How strongly the wind pushes the banana sideways. */
export const WIND_DRIFT = 0.00075;
/** Converts the 15-100 power slider into launch speed. */
export const SPEED_SCALE = 0.115;
/** Spin applied to the banana sprite per frame. */
export const SPIN = 0.35;

// --- aiming preview --------------------------------------------------------
/** Dots drawn in the predicted trajectory. */
export const AIM_STEPS = 16;
/** Distance travelled per preview dot (in preview-space units). */
export const AIM_DOT_STEP = 13;
/** Horizontal drag and vertical lift used by the preview integration. */
export const AIM_DRAG = 0.985;
export const AIM_LIFT = 0.035;
/** Larger value = the trail fades out sooner. */
export const AIM_FADE = 18;

// --- damage ----------------------------------------------------------------
/** Base crater radius, plus a bit of randomness. */
export const CRATER_RADIUS = 18;
export const CRATER_JITTER = 6;
/**
 * Frames a banana is harmless to its own thrower. It leaves the fist inside the
 * throwing arm's own hitbox, so without this a weak shot kills you on frame one.
 */
export const SELF_HIT_GRACE_FRAMES = 14;
/**
 * Step length of the hit sweep (px). At full power a banana covers ~11px per
 * frame, so a single point test can both step over a thin arm and come to rest
 * short of what it hit. 4px is finer than the thinnest part of the gorilla.
 */
export const HIT_SWEEP_PX = 4;

/**
 * Where the banana leaves the hand, relative to the gorilla's feet. Shared with
 * city.js, because the perch has to be chosen around where the fist will be — a
 * gorilla whose fist is over the next building's roof cannot shoot at all.
 */
export const FIST_DX = 30;
export const FIST_DY = -6;

// --- the grenade -----------------------------------------------------------
/**
 * Frames before a grenade goes off, counted from the moment it leaves the hand.
 *
 * This has to outlast the longest flight, or the grenade never reaches anything:
 * a full-power shot is in the air for about 226 frames, and a fuse shorter than
 * that turns the weapon into a banana that explodes early and high, which is
 * strictly worse than the banana. A lobbed shot does not wait this long — see
 * GRENADE_SETTLE_FUSE.
 */
export const GRENADE_FUSE_FRAMES = 240;
/**
 * Fuse left once the grenade has come to rest. A grenade lying on a rooftop is
 * not going anywhere, so there is nothing to be gained by making the player wait
 * out the rest of a flight fuse they were given for a throw that missed long.
 */
export const GRENADE_SETTLE_FUSE = 36;
/** Speed a grenade keeps when it bounces off a building. Damped, but not dead. */
export const GRENADE_BOUNCE = 0.42;
/**
 * Below this speed a contact is not a bounce, it is the grenade settling. Without
 * it a grenade that has rattled itself to a stop on a rooftop keeps "bouncing"
 * every frame: sixty clinks a second and no visible movement.
 */
export const GRENADE_REST_SPEED = 0.6;
/**
 * How far the blast reaches. With a banana a near miss is a miss; with a grenade
 * it is a kill, and that difference is the entire reason the thing exists.
 */
export const GRENADE_BLAST_R = 34;
/** Its crater, next to a banana's. */
export const GRENADE_CRATER_SCALE = 2.1;

// --- the ulti charges -------------------------------------------------------
/**
 * The most charges of one special a bag will stack up, and the only number the charges
 * have left.
 *
 * There used to be an ulti meter beside it: a bar that filled as you played — a hit, a near
 * miss, a round won — and paid out a charge of every ult you owned. It decided *when* you
 * could spend what you had earned, and for most of a match the answer was "not yet", which
 * is a warmup dressed as a reward. It is gone; a crate is what hands a charge back now.
 */
export const CHARGE_CAP = 3;

/**
 * Where a share link points. The canonical address rather than window.location, so a
 * link shared off a laptop's localhost copy still sends people to the real game — and
 * so the preview card the networks build comes from the tags on that page.
 */
/**
 * How often the CPU reaches for one of its specials.
 *
 * Deliberately low. The kit exists so the CPU is not outgunned, not so it becomes
 * a problem: the first version of this sat at 0.35 and 0.3, and a measurement of
 * four hundred goes of maybeCpuTurn showed it arming something on 52 turns in a
 * hundred — the bag was empty within the first few turns and the rest of the
 * match was played plain. At these numbers the same measurement reads 28 turns in
 * a hundred, so the kit lasts a match instead of a moment, and a CPU that answers
 * everything stays nobody's idea of a good evening.
 */
export const CPU_ULT_CHANCE = 0.15;
export const CPU_GUARD_CHANCE = 0.15;

/**
 * How well the CPU shoots.
 *
 * It used to guess: a flat 35 degrees plus or minus eight, and a power formula that reached
 * the ceiling on every long shot. Measured over twelve different cities it hit 0 of 72 — the
 * angle was roughly right and the power was half as much again too much, so the banana
 * sailed over the rival's head every single time. It solves the shot now: it throws it in
 * its head until it finds one that lands, which every city has plenty of (measured, 44 to 61
 * clear solutions apiece), and then throws that off by the scatter below.
 *
 * The scatter is the difficulty dial, and it is measured in AIM rather than in landing
 * distance, because the aim is the thing being got wrong: one degree at 700px is about 12px
 * of miss, and the rival is 60px across, so these numbers look small and are not.
 *
 * Y estos números salen de medir la curva con las MISMAS cuatro ciudades (659, 668, 834 y 907 px
 * de separación) y SESENTA tiros por punto, porque con doce el margen de error era de veinticinco
 * puntos y las decisiones salían al azar:
 *
 *   (3°, 2) 58%   (4°, 3) 48%   (5°, 4) 37%   (6°, 6) 29%   (8°, 10) 17%
 *
 * Con eso, y con lo que pidió el jugador —equitativo y con ventaja suya, que la máquina no atine
 * tanto—, los tres niveles quedan en un 15%, un 23% y un 45% de aciertos (medido con sesenta tiros
 * por nivel y las mismas cuatro ciudades). Antes medían 46%, 62% y 96%: el `hard` de fábrica
 * acertaba 23 de 24 tiros y el `normal`, que es el de fábrica, casi dos de cada tres — contra un
 * humano que necesita dos o tres tiros para calibrar el viento.
 *
 * Casi toda la dispersión va en el ángulo. Un error de ángulo cae unas veces largo y otras corto,
 * mientras que un error de potencia deja el plátano siempre corto, y eso se aprende en dos tiros.
 */
export const CPU_LEVELS = [
  { id: 'easy', scatterDeg: 8, scatterPower: 10, thinkMs: 900, grabMs: 6000 },
  { id: 'normal', scatterDeg: 6, scatterPower: 6, thinkMs: 700, grabMs: 4500 },
  { id: 'hard', scatterDeg: 4, scatterPower: 3, thinkMs: 500, grabMs: 3000 },
];
export const CPU_LEVEL_DEFAULT = 'normal';
/** Longest a simulated throw is followed before it counts as gone. */
export const CPU_SOLVE_FRAMES = 500;

/**
 * How long a player has to throw, and the choices the setup screen offers.
 *
 * Zero is off, and it is one of the choices rather than a hidden setting: a turn
 * clock is a match rule like FIRST TO n, and a table of two people sharing a
 * keyboard may not want one at all. Fifteen seconds is the default because it is
 * long enough to aim a shot and short enough to notice.
 *
 * When it runs out the turn is lost, which is the only answer that is fair: firing
 * with whatever the sliders happen to say would reward walking away.
 */
export const TURN_SECONDS_DEFAULT = 15;
export const TURN_SECONDS_CHOICES = [0, 15, 30, 45];
/** From how many seconds left the clock starts shouting. */
export const TURN_RUSH_SECONDS = 5;

// --- the sheep --------------------------------------------------------------

/**
 * The sheep, in canvas px and in frames, thrown the way the machines of 1995 threw one.
 *
 * It does not fly: it hops, and what it hops along is the rooftops. The hop is high enough
 * to clear a gorilla and to climb a building of the usual height, and short of a tower —
 * and a sheep stopped by a tower is the sheep stuck: hopping on the spot against the wall
 * and bleating at it until its own fuse runs out. That is the whole of what it does when
 * the city is against it, and it is deliberate: it walks where you threw it, and no further.
 *
 * The numbers are derived from GRAVITY and not from taste, because the first version of them
 * was guessed and the first version of them was wrong: at this file's 0.072 a hop of `vy` 10.5
 * peaks 765 px up, which is more than the canvas is tall, and hangs there for five seconds —
 * two hops and 130 px of ground in the whole of its fuse. What a hop is worth is
 * `vy² / (2 · GRAVITY)` px of height and `2 · vy / GRAVITY` frames of air, so 5 and 1.6 give
 * a 174 px leap that lasts 2.3 s and lands 220 px along. Change GRAVITY and these move.
 */
export const SHEEP_HOP_VY = 5;
/**
 * How much of a step up is a lip and how much is a wall, in px.
 *
 * A sheep does not climb. The hop is 171 px of bounce, so physically it could get on top of
 * most of this city — and it does not, on purpose: a step down is a hop, a step up of more
 * than a lip is a wall, and a wall turns it round. That is what makes a hollow something it
 * walks into and has to find its way out of, instead of a place it climbs out of on the first
 * try. Eight px is a kerb; more than that and it has met a building.
 */
export const SHEEP_STEP_UP = 8;
/**
 * A hop every ~140 frames at this speed covers about 220 px of ground — four or five hops to
 * cross the city, and each one visible from the other end of it. Big jumps, as asked.
 */
export const SHEEP_SPEED = 1.6;
/**
 * How long it may keep at it before it goes off on its own. Five hops' worth, so a sheep with
 * a clear run can still cross the whole map before the fuse gets there — which is the point:
 * the fuse is for the sheep that is stuck, not for the one that is doing well.
 */
export const SHEEP_FUSE_MS = 12000;
/**
 * When the countdown over the sheep appears, in ms of fuse left.
 *
 * The bleat on every hop is the clock the player has while the animal walks, and it is enough
 * while there is nothing to do about it. It is not enough for the last stretch, where the
 * difference between four seconds and one is the difference between stepping out of the way
 * and wearing it — so the last five get counted out loud.
 */
export const SHEEP_FUSE_COUNTDOWN_MS = 5000;
/** Past the last building there is nothing left to walk into, and it goes quietly. */
export const SHEEP_MARGIN = 60;
/** Its bang, relative to a banana's. Bigger, because it is a bomb with legs. */
export const SHEEP_CRATER_SCALE = 1.5;

export const SITE_URL = 'https://qbasicgorillas.com/';

// --- the ball ---------------------------------------------------------------
/**
 * The ult that throws a ball instead of a banana, and does not tell you which one
 * until it is in the air.
 *
 * Each ball flies its own way, which is the whole of the joke: the skill is reading
 * which one you were given, not choosing anything. `gravity` multiplies whatever the
 * mode set, `wind` is how much of the wind reaches it, and `crater` is against a
 * banana's. The icon is not a word, so it needs no translating.
 */
export const BALLS = [
  { id: 'basket', icon: '🏀', r: 7, gravity: 1.25, wind: 0.5, crater: 1.3 },
  { id: 'football', icon: '⚽', r: 6, gravity: 1, wind: 1, crater: 1 },
  { id: 'rugby', icon: '🏉', r: 6, gravity: 1.5, wind: 0.35, crater: 1.5 },
  { id: 'tennis', icon: '🎾', r: 5, gravity: 0.75, wind: 2.2, crater: 0.6 },
];

// --- reward crates ----------------------------------------------------------
/**
 * The crate a Worms map drops in. One falls per round, somewhere along the skyline,
 * and either side can break it with a banana.
 *
 * Where it comes down is drawn from the round's own dice, like the buildings and
 * the moon, so a shared match code rebuilds it in the same place.
 */
export const CRATE_W = 22;
/**
 * The box a falling crate has to touch for the gorilla under it to take it.
 *
 * Measured against the FIGURE this time, because guessing it has now cost two
 * reports of the same thing — "the reward is grazing him and he does not take it".
 * Drawn alone on an empty canvas, a gorilla covers 69px across: 32px to the left of
 * his centre and 36px to the right of it, the fist included, and the widest species
 * (the rhino) is 34 and 38. The box used to reach 17px, so there was a band 19px
 * wide on each side where the crate lay across his arm, visibly touching him, and
 * was left on the roof. Thirty-four was never a measurement of anything: it is the
 * width of the body without the arms, and the arms are what a crate lands on.
 *
 * 76 is the crate's own 22 plus two of the widest reach, so a crate that looks like
 * it is lying on him is his, and one that looks like it missed, is not. Height wants
 * no such care: the figure stands 41px above his feet at the tallest, well inside
 * the 48 the box gives him.
 */
export const CRATE_PICKUP_W = 76;
export const CRATE_PICKUP_H = 48;

/**
 * Cuánto puede fallar el dedo al pulsar el regalo, en coordenadas del campo.
 *
 * La caja mide 22x18 en un campo de 960x540, que en un teléfono de lado son 16x13 pixels de
 * cristal: un dedo no acierta ahí ni queriendo. Medido a 844x390, la escala del campo es 0,72,
 * así que 46 son 33 pixels de cristal — un dedo estirado y no una punta de lápiz. En un monitor
 * son 46 pixels de verdad, que es holgado pero no absurdo: el regalo es lo único del campo que
 * responde a un toque, y el doble clic de pantalla completa no lo pisa.
 */
export const CRATE_GRAB_R = 46;
export const CRATE_H = 18;
export const CRATE_SPAWN_Y = -30;
export const CRATE_FALL_SPEED = 2.6;
/** How often a round has no crate at all, as a percentage. */
export const CRATE_ABSENT_PERCENT = 35;

// --- el bumerán -------------------------------------------------------------
/**
 * Los fotogramas que vuela libre antes de empezar a tirar de vuelta.
 *
 * Son 70 y no un número redondo por lo que sale de ellos: a la velocidad de un tiro normal el
 * bumerán se abre unos 500 px antes de darse la vuelta, así que llega de sobra a un rival de la
 * mitad del campo y se queda corto con uno pegado al borde. Que no siempre llegue es parte del
 * tiro: lo que se viene a buscar aquí no es el alcance, es no perder la carga.
 */
export const BOOMERANG_OUT_FRAMES = 70;
/**
 * Cuánto tira hacia quien lo lanzó, por fotograma y por píxel de distancia.
 *
 * Un muelle muy flojo a propósito: la fuerza apunta al tirador desde que se abre, y con esto la
 * trayectoria sale, se abre y regresa sola, que es la mitad de lo que se lee como un bumerán. Más
 * fuerte y volvería de golpe en línea recta; más flojo y no volvería.
 */
/**
 * Y en la vuelta la gravedad pesa menos.
 *
 * Es lo que le deja llegar: con la gravedad entera el bumerán se caía al suelo a mitad de regreso
 * —medido, no volvía a acercarse a la mano después de salir— y un bumerán que no vuelve es una
 * piedra con buena fama. Con esto flota de vuelta sin perder el alcance de la ida, que sigue
 * siendo el de un tiro normal.
 */
export const BOOMERANG_RETURN_GRAVITY = 0;
export const BOOMERANG_PULL = 0.001;
/**
 * Lo que frena el aire en la vuelta, por fotograma.
 *
 * Sin esto el regreso no era un regreso: con la gravedad apagada y un muelle sin freno, lo que
 * queda es un oscilador armónico — una ORBITA alrededor de la mano, con la energía del tiro
 * intacta — y sólo se cogía si la elipse pasaba a menos de `BOOMERANG_CATCH_R` del dueño. Medido:
 * un tiro corto seguía dando vueltas a los 3000 fotogramas (cincuenta segundos) y a 220 px de la
 * mano. Es lo que se lee como «el bumerán se queda ahí para siempre».
 *
 * Con esto la vuelta pierde energía como la perdería en el aire y entra en la mano sola. No más
 * freno: por debajo de 0.9 la ida se frena antes de darse la vuelta y el tiro pierde alcance.
 */
export const BOOMERANG_DRAG = 0.96;
/**
 * Lo que puede tardar en volver antes de darse por perdido, contado desde que se da la vuelta.
 *
 * No es una regla de juego, es el seguro de que un tiro no se queda colgado: con el freno de
 * arriba vuelve en la mitad, pero un tiro que por lo que sea no vuelva tiene que acabar — el
 * turno pasa y se acabó. Sin esto, cualquier caso raro (una órbita que no cierra, un gorila que
 * muere justo en la vuelta) dejaba la partida esperando un plato que no llega nunca.
 */
export const BOOMERANG_HOME_FRAMES = 150;
/**
 * Por dónde vuelve: a esta altura SOBRE la cabeza, y bajando a medida que se acerca.
 *
 * El regreso era una línea recta hacia la mano que atravesaba la primera torre que pillara — y
 * como eso se veía fatal, la vuelta entera se dejó SIN colisiones, con lo que el bumerán
 * atravesaba la ciudad entera como un fantasma. Lo que hay aquí es el arco: el objetivo es un
 * punto alto mientras está lejos (por encima de los tejados del camino) que se va bajando a la
 * mano conforme llega, así que las colisiones pueden volver a estar puestas.
 */
export const BOOMERANG_HOME_LIFT = 130;
/** Cuánto sube ese punto por píxel de distancia horizontal. Ver BOOMERANG_HOME_LIFT. */
export const BOOMERANG_HOME_SLOPE = 0.45;
/**
 * Los fotogramas que tarda el arco en apagarse, contados desde que empieza la vuelta.
 *
 * Sin esto la altura del regreso sólo dependía de la distancia horizontal, y eso deja al bumerán
 * orbitando sobre su dueño sin entrar nunca en el radio de captura: a 130 px por encima de la mano
 * se está fuera por mucho que uno se pare. Medido con viento cero y la misma ciudad, volvían dos de
 * cada seis tiros y los otros cuatro morían en el tope de `BOOMERANG_HOME_FRAMES`.
 */
export const BOOMERANG_HOME_ARC_FRAMES = 60;
/**
 * A qué distancia de la mano se coge. Una distancia y no la caja del cuerpo, y eso es una
 * corrección medida: el bumerán volvía a pasar a treinta píxeles de su dueño, la caja del cuerpo
 * no lo tocaba, y seguía de largo hasta estrellarse — un tiro que vuelve y no se coge, que es
 * justo lo contrario de lo que promete.
 */
export const BOOMERANG_CATCH_R = 48;
/** Lo rápido que gira. Un bumerán que no gira no se lee como un bumerán. */
export const BOOMERANG_SPIN = 0.46;
/**
 * Una caja de cada cuatro trae una vida extra en vez de una carga, y caben dos.
 *
 * La vida no se guarda como las cargas: se gasta sola, sin elegirla, en el momento en que un
 * golpe iba a acabar con el que la tiene. Es el colchón que pidió el jugador —"que caigan
 * vidas extra para aguantar ataques y no morir"— y dos es la dosis: una es un susto que se
 * olvida, y con tres la ronda deja de tener tensión.
 */
export const CRATE_LIFE_CHANCE = 0.25;
export const LIFE_CAP = 2;

// --- the spring -------------------------------------------------------------
/**
 * A spring planted under your own feet: the next enemy banana that lands on it is
 * thrown straight back over the thrower's head.
 *
 * The shield's shape and the shield's test — a rectangle that is measured on
 * exactly the pixels it is painted on — but it does it the other way round with
 * what it catches: the shield stops the shot, the spring returns it.
 *
 * And it is a ride rather than a fence. It goes under the gorilla rather than in
 * front of him, and he climbs on and bounces on it for the turn it lasts — the
 * ornament on a car dashboard, which is where the joke comes from and which is why
 * the spring and the dance travel together: a spring nobody stands on is a fence.
 */
export const SPRING_W = 28;
export const SPRING_H = 9;
/** How hard it throws the banana back, as an upward kick in px per frame. */
export const SPRING_KICK = 4;
/**
 * The dance: a lean and a stretch, both from the same beat.
 *
 * He is the clown on the jack-in-the-box lid, and the whole of what that looks like is that he
 * never leaves the spring: the coil stretches under him, the coil leans, and the two of them go
 * together — a spring at its tallest is a spring standing straight, and a spring that has folded
 * over is a short one. So it is ONE beat rather than a bounce with a sway behind it, and the
 * numbers come out of it: how far it has folded is the lean, and how far from folded it is is
 * the stretch.
 *
 * That is what the dance was missing. The bounce used to run at twice the sway and simply lift
 * him: five pixels of air between his feet and a plate that never moved, which is a gorilla
 * floating above a spring rather than a gorilla standing on one. And it is meant to be a
 * pantomime: the coil goes from nine pixels tall to twenty and leans a third of that to one
 * side, which is how far a spring has to move before anybody watching calls it a spring at all.
 *
 * Both numbers go into the pose, so the drawing and the hit test bob together: while he is up
 * there he is a moving target, which is the honest thing for a gorilla balancing on a spring to
 * be.
 */
export const SPRING_DANCE_SWAYS_PER_SECOND = 1.3;
/** How far the coil stretches at the top of the beat, in px. The plate goes up with it. */
export const SPRING_DANCE_UP = 11;
/** How far the plate swings to one side when the coil has folded over, in px. */
export const SPRING_DANCE_SWAY = 11;
/** How far the head leans into the swing, in px: the body follows the plate a little. */
export const SPRING_DANCE_LEAN = 5;

// --- the shield -------------------------------------------------------------
/**
 * A mediaeval shield, held up in front for one enemy shot.
 *
 * It protects by being *in the way* rather than by making anybody harder to hit:
 * it is drawn, it is solid, and a banana that meets it stops there and does
 * nothing. So the rule the rest of the game keeps — what you see is what you can
 * hit — still holds, and the answer to it is visible: aim over or around the edge.
 */
export const SHIELD_W = 17;
/**
 * How tall it is, now that the point is the thing in his hand: the shield rises from the
 * fist to just over the shoulder, a buckler rather than a door. It was 46, which stood from
 * his hip to above his head and read as a plank propped up beside him.
 */
export const SHIELD_H = 28;
/**
 * How much of the bottom comes to a point, in px.
 *
 * A shield is a slab that narrows, and the point is the whole of what says which way up it
 * is being held: a rectangle with a cross on it reads as a placard. 10 of the 28 is a
 * heater's proportion — the point is a third of it — and it is drawn INSIDE the rectangle
 * the hit test uses, so what a shield blocks does not change by a pixel.
 */
export const SHIELD_POINT = 10;

export const SPLASH_ENTER_MS = 700;
export const SPLASH_LOOK_MS = 900;

// --- the greeting pair, leaning in from the wings ---------------------------
//
// A second pair, in front of the fight and in front of everything else on the front
// end: they come in from the side margins with the bottom of the frame under their
// feet, which is what reads as being nearer than the buildings. Nothing to do with
// the two throwing bananas behind them — these are only here to wave.
export const GREET_SLIDE_MS = 420;
export const GREET_WAVE_MS = 2400;
/** The wait between one visitor and the next, which is never the same twice. */
export const GREET_GAP_MIN_MS = 3200;
export const GREET_GAP_MAX_MS = 9000;
/** How far in from the margin they lean, and how far outside it waits. */
export const GREET_INSET = 24;
/**
 * How much of that lean the margin takes back, in px, and how much of it varies.
 *
 * The sideways ones lean about a spot around their hips, so an inset of 24 puts both slippers of
 * a whole puppet on the glass and it reads as standing on the edge of the picture rather than as
 * coming round it. Pulled out by this much, the frame cuts the feet and the ankles while the rest
 * of the body still leans well inside: that is the pose they are meant to be caught in.
 *
 * The spread is what keeps one entrance from being the last entrance. The lean used to be the
 * same amount of body on every visit -- measured at 86 px of it, to the pixel, on four goes in
 * a row -- so a fresh place along the edge still arrived in the same pose showing the same
 * amount of puppet, which is what reads as always coming out in the same spot.
 *
 * The floor is not zero and not a matter of taste: the sprite is anchored about the hips, so on
 * a 45 degree lean its feet hang about 16 px to the left of the anchor. Under 16 the slippers
 * are back on the glass, which is the pose this is here to avoid.
 *
 * Neither number applies to the one on the bottom edge: he is standing in the picture and the
 * whole of him is meant to be in it.
 */
export const GREET_HIDE = 12;
export const GREET_HIDE_SPREAD = 12;
/** Far enough out that the whole puppet waits outside the frame rather than half in it: at
 *  1:1 the sprite is about 68 px across, so this clears the half of it either side of its
 *  anchor. It was 150 while they were drawn at double. */
export const GREET_OFF = 70;

/**
 * How much bigger the puppets on the front end are than the animals in a match.
 *
 * One: life size, which is how they were drawn before being doubled. Doubling them was a
 * judgement that at 1:1 they read as two animals standing about in a field with no rooftops
 * rather than as an invitation, and the player asked for the small ones back — they are the
 * front end's hosts leaning in from the margins, not characters in the picture.
 *
 * Whole numbers only, and that is not fussiness: the sprite is drawn in game pixels, so a
 * scale of 2 makes every pixel of it a square of two, and a scale of 2.6 makes some of them
 * two and some of them three. A blown-up sprite with an uneven grid is the one thing this
 * art cannot survive.
 */
export const GREET_SCALE = 1;
/**
 * How far along the bottom edge they are allowed out, measured from the corners.
 *
 * The middle of a horizontal edge is the one place where a gorilla entering the frame
 * stops reading as leaning in from the edge and starts reading as standing in the middle
 * of the picture, with the card on top of it.
 */
export const GREET_CORNER = 150;
/** The top of the band the sideways ones come out of. */
export const GREET_HIGH = 110;
/**
 * Cuánto por encima del borde de abajo va su ancla, para que los pies toquen el borde.
 *
 * El muñeco se ancla por las caderas, así que con los pies en el suelo del cuadro el ancla
 * queda esto más arriba. Era 516, que sobre un lienzo de 540 son exactamente estos 24 y no un
 * número propio: al crecer el mundo a 720 se quedaron colgados doscientos píxeles en el aire,
 * que es como se reportó. Ahora se mide desde el borde, que es lo que siempre quiso decir.
 */
export const GREET_FEET = 24;
/** And the one hanging in over the top of the frame has its feet still outside it. */
export const GREET_TOP_Y = 10;
/** How often a lob goes up, and how long each one takes to come down. */
export const SPLASH_THROW_EVERY_MS = 1600;
export const SPLASH_THROW_FRAMES = 62;
/** How far either side of the middle they aim, so the two arcs never meet. */
export const SPLASH_AIM_SPREAD = 130;
/**
 * How much each lob is allowed to differ from the last.
 *
 * Without these the scene is a loop rather than an anecdote: the launch point,
 * the target, the flight time and the turn-taking were all fixed, so every throw
 * was the identical parabola for as long as the front end was up.
 */
export const SPLASH_AIM_JITTER = 0.75;
export const SPLASH_DROP_JITTER = 70;
export const SPLASH_FLIGHT_JITTER = 26;
export const SPLASH_GAP_JITTER_MS = 700;
/** Chance that the same gorilla goes again instead of taking turns. */
export const SPLASH_REPEAT_CHANCE = 0.3;
/**
 * How fast a thrown banana tumbles, in radians per frame, and which way.
 *
 * This was a fixed 0.16 for a while, which meant every banana in the opening scene
 * turned over at exactly the same speed and the tossing read as a loop no matter how
 * much the arcs were jittered. The arcs are half the look; the spin is the other half.
 */
export const SPLASH_SPIN_MIN = 0.08;
export const SPLASH_SPIN_MAX = 0.3;
/** How far below his perch a gorilla starts, so he rises into place on the way in. */
export const SPLASH_RISE = 26;
/**
 * How much further down than SPLASH_RISE a gorilla may start, in px.
 *
 * The two of them walk on from their own margins, and where they cross the edge of the
 * picture is decided by how far below the roofline they start. It used to be exactly
 * SPLASH_RISE every time, so a player returning to the front end over and over saw the same
 * entrance in the same spot — the one thing about the greeting that never changed. See
 * `startSplashScene`.
 */
export const SPLASH_ENTRY_SPREAD = 120;
/** Where the lobs come down: over the roofs, well short of the rival. */
export const SPLASH_LANDING_Y = 380;

// --- the leap --------------------------------------------------------------
/**
 * The leap: he throws himself into the air and throws the banana from the top of
 * the jump, at a point that was fixed before he left the ground.
 *
 * Timed in frames rather than milliseconds, because the banana is let go on an
 * exact frame of the jump and the frame loop that steps the banana steps this.
 */
export const LEAP_CROUCH_FRAMES = 8;
export const LEAP_AIR_FRAMES = 30;
/** How high he gets, in px, and how far he dips before he goes. */
export const LEAP_HEIGHT = 42;
export const LEAP_DIP = 5;
/**
 * How long the thrown banana is in the air, in frames.
 *
 * Measured, and it is NOT the difficulty dial I assumed it was. A longer flight
 * turned out to be easier, not harder: 70 frames connected 95% of the time and
 * 130 connected 100%, because the banana spends longer in the region the hitbox
 * occupies and the rival's idle movement is smaller than his own hitbox. Kept
 * long because the high lob is the nicer thing to watch.
 */
export const LEAP_FLIGHT_FRAMES = 130;
/**
 * How far off the target the throw can land, in px — and it is flavour, not
 * balance. Recorded honestly because it was meant to be the difficulty dial and
 * is not one: at 30px the leap still kills 93.7% of the time, against 100% with no
 * scatter at all.
 *
 * Nothing aims an auto-targeting shot badly enough in this game to make it hard,
 * because the gorilla's hitbox is about 60px across and barely moves relative to
 * its own width. The scatter is kept because a mid-flip throw that lands perfectly
 * every time reads as a magnet rather than a throw.
 */
export const LEAP_SCATTER = 30;

// --- the drone -------------------------------------------------------------
/** The height it crosses at, and how fast (px per frame). */
export const DRONE_Y = 58;
export const DRONE_SPEED = 2.6;
/**
 * How long the rival stays painted after the drone has called it out. Has to
 * miss the next turn: the point of a UAV is that it helps the shot *after* the
 * one you called it in on.
 */
export const UAV_PAINT_MS = 13000;

/**
 * La bomba que suelta el dron al pasar sobre el que lleva pintado.
 *
 * El dron estaba de adorno —marcaba a un rival que ya estás viendo y alargaba una mira de 93 px
 * cuando entre los dos monos hay 748— y así lo dijo el jugador: «hay un dron que sale que no
 * hace na». Ahora es un arma: cae en vertical sobre el pintado y a quien pilla dentro del radio
 * se le acaba la ronda, salvo escudo levantado o seta, que son las dos defensas que ya paran
 * cualquier otro tiro. De un solo uso, como `leap`: una ronda regalada por partida, y el que la
 * recibe tiene con qué pararla.
 */
/** A esta distancia del pintado, en el eje x, suelta la carga. */
export const DRONE_DROP_X = 12;
/** Velocidad inicial de la caída, en px por fotograma. Lo demás lo pone la gravedad del modo. */
export const DRONE_BOMB_VY = 1.4;
/** El radio que mata. La granada mata con 34 y es un arma apuntable: esta cae sola, así que 30. */
export const DRONE_BLAST_R = 30;
/** Y lo que abre en el tejado, comparado con el 1 de la granada. */
export const DRONE_BOMB_CRATER = 1.3;

// --- banter ----------------------------------------------------------------
/** How long a speech bubble stays up, and how long it takes to pop in. */
export const BUBBLE_MS = 2300;
export const BUBBLE_POP_MS = 150;
/** Gap between bubble and head, and the padding inside it. */
export const BUBBLE_GAP = 12;
export const BUBBLE_PAD = 5;
/**
 * The canvas needs a full stack for exactly the reason the CSS does: a single
 * family that does not resolve drops the whole font shorthand and the browser
 * falls back to a serif. That is where the serif buttons came from.
 */
export const UI_FONT = 'ui-monospace, "Cascadia Mono", SFMono-Regular, Menlo, Consolas, monospace';

// --- the splash screen, and the screen after a kill ------------------------
/**
 * Loading tips. Every game of this kind has them, they are always faintly
 * useless, and they are the cheapest way to tell a player something true about
 * the game while pretending to fill time.
 */
export const SPLASH_TIP_COUNT = 6;

/** How long the kill caption holds, and how long it takes to fade either way. */
export const WASTED_MS = 1500;
export const WASTED_FADE_MS = 280;
/** Its wash over the scene, and the size of the lettering itself. */
export const WASTED_WASH = 0.42;
export const WASTED_FONT_PX = 58;
export const WASTED_TRACKING = 10;

/**
 * The round announcement, in the arcade manner: ROUND n, then FIGHT.
 *
 * Two beats rather than one, because the pause between them is the whole trick:
 * it is the moment the thing stops being a menu and starts being a fight. These
 * are the holds; the zoom-in is timed in announce.js.
 */
export const ANNOUNCE_ROUND_MS = 1150;
export const ANNOUNCE_FIGHT_MS = 700;
export const ANNOUNCE_FONT_PX = 56;
export const ANNOUNCE_FIGHT_FONT_PX = 66;
export const ANNOUNCE_TRACKING = 8;
/** How long the number takes to stop growing. Nothing in this game arrives at
 * once, and a caption that does reads as a stalled frame. */
export const ANNOUNCE_ZOOM_MS = 260;
/** How far past its size the number starts. Starting at 1 and growing would push
 * it in from nowhere; starting over-size and settling is the arcade move. */
export const ANNOUNCE_ZOOM_FROM = 1.55;
/** The wash behind the number. FIGHT gets none: it lands on the city, not on a
 * darkened frame, because by then the fight has started. */
export const ANNOUNCE_WASH = 0.34;
/**
 * The nudge that says whose turn it has just become.
 *
 * One beat, no wash, and smaller than the round call on purpose: the round call is
 * said once a round and this one is said once a turn, and forty full-screen shouts
 * is not a match. Nor does it cover anything up — the field is exactly what the
 * player has just been asked to look at.
 *
 * No colour here: the lettering takes the colour of the gorilla whose turn it is,
 * which is the fastest way of saying which of the two of you it means.
 */
export const ANNOUNCE_TURN_MS = 1000;
export const ANNOUNCE_TURN_FONT_PX = 34;
export const ANNOUNCE_TURN_TRACKING = 5;
export const ANNOUNCE_TURN_ZOOM_MS = 160;
// --- scenery ---------------------------------------------------------------
/**
 * Columnas de torres lejanas, y el ancho que ocupa cada una.
 *
 * Eran 28 porque 28 x 38 = 1064, un poco más que el lienzo de 960, y las últimas
 * siluetas quedaban fuera del cuadro a propósito. El mundo mide ahora 1280, y el
 * skyline tiene que llegar tan lejos como la ciudad: 38 x 38 = 1444, que es la
 * misma proporción de sobra que dejaba antes.
 */
export const SKYLINE_COLUMNS = 38;
export const SKYLINE_COLUMN_WIDTH = 38;
/**
 * Tantos como hacían falta para el mismo grano de antes.
 *
 * Eran 90 sobre un cielo de 960 x 250 — una estrella cada 2.667 píxeles —, y el cielo de ahora
 * mide 1280 de ancho por los 425 que hay desde arriba hasta la primera torre del fondo. Con 90
 * el campo salía dos veces y media más ralo y se veía vacío.
 */
export const STAR_COUNT = 204;
/**
 * Cuánto se les quita de brillo a las estrellas.
 *
 * Un 15%, que es lo que se pidió. El cielo de 1280 tiene más estrellas a la vista que el de 960
 * —y más gordas, que a 0,8 de pantalla hacían falta para no desaparecer—, así que el campo salía
 * más brillante de lo que era. Va encima del parpadeo de las doradas y no en su lugar: la dorada
 * tiene que seguir parpadeando, y seguir siendo oro.
 */
export const STAR_DIM = 0.85;

/**
 * How long the sky takes to slide its own width, in seconds.
 *
 * The stars are on a clock now: the whole field drifts westward and comes back in at
 * the other edge, which is what a night sky does as the earth turns under it. The real
 * one takes a day; this one takes three hours, eight times faster, and even then it is
 * measured at 5.3px a minute — a twelfth of a pixel a second, which cannot be read as
 * motion at all from one frame to the next. It is there for the player who watches one
 * star against a rooftop and finds it somewhere else a few minutes later, which is the
 * whole of what "the sky is moving" means.
 *
 * Three hours and not one: a session here is minutes, and a sky that took a full day to
 * cross would be a claim nobody could ever check. See `drawBackground`.
 */
export const SKY_TURN_SECONDS = 3 * 3600;

/**
 * How often the people in the buildings change their minds about a light.
 *
 * Each building re-rolls its own windows every WINDOW_TICK_SECONDS, staggered by its
 * address, and then any one window only has WINDOW_TOGGLE_CHANCE of being a window
 * that turns. Measured over ten minutes in two different cities: 133 changes in one and
 * 215 in the other — a light going on or off somewhere along the skyline every three or
 * four seconds, four times out of five a single pane, and any single window changing
 * about once every half hour. (The city is drawn per round, so a count is a range and not
 * a constant: 12 buildings, 355 to 452 windows.)
 *
 * The two numbers are not independent, and saying so is the point: they are the same two
 * constants, so twice the traffic through the city is twice the chance of catching one
 * window at it. What the stagger buys is not slowness but spread — a light here, then one
 * three doors down, rather than the whole street blinking together.
 *
 * And they are not the same knob. The chance is the one that says HOW MANY windows go at
 * once; the tick only says how often the question is asked. This has been raised twice for
 * "the windows have no activity at all" and both times by the tick — a light every twenty
 * seconds measured at 62 changes in twenty minutes, then every four or five at 72 in ten —
 * and those two are invisible for a reason that is not the rate: one pane seven pixels
 * wide, out of four hundred, while the eye is on the gorillas. The third attempt pushed
 * both numbers at once and went too far: a twentieth of the panes on an eight-second tick
 * is a light a second AND two or three windows of the same building going together, which
 * reads as a screen refreshing rather than as a street. Reported as exactly that — "too
 * fast, and too many at once".
 *
 * So the two are set apart now: one window in eighty is the answer —the number that says HOW MANY
 * go at once, and the one that has to stay small— and the tick is the question.
 *
 * Y el tick baja a CUATRO segundos, cuarta vez que se sube el ritmo por lo mismo. Medido con el
 * anterior (dieciséis): catorce cambios en veinticinco segundos en toda la ciudad, pero repartidos
 * de forma que **la mayoría de los diecisiete edificios cambiaba UNA ventana cada veinticinco
 * segundos** —contado edificio por edificio, y salió esto: [1,0,0,1,0,1,1,4,0,1,1,1,1,1,0,0,1]—.
 * Con cuatro segundos cada edificio cambia una ventana cada seis o siete.
 *
 * Lo que NO arregla, y hay que decirlo porque es la razón de que se haya subido cuatro veces: el
 * cambio sigue siendo de siete por ocho píxeles de lienzo —**cinco por seis en pantalla**, medido
 * con el lienzo a 988 de ancho— así que en un edificio de los diecisiete se ve poco. Lo que se
 * vería de verdad es una habitación encendida: dos o tres paneles juntos. Mientras eso no se haga,
 * subir el tick es subir el número de veces que pasa algo que no se ve.
 *
 * See `advanceWindows` for why the re-roll is staggered at all: a whole city consulting its watch at
 * once would be one flash, not a neighbourhood.
 */
export const WINDOW_TICK_SECONDS = 4;
export const WINDOW_TOGGLE_CHANCE = 0.012;

/**
 * Lo que tarda una ventana en pasar de apagada a encendida, en milisegundos.
 *
 * El lienzo del edificio es una foto, y una foto que cambia de un fotograma al siguiente es un
 * parpadeo, no un vecino: medido con el cambio instantáneo, la ciudad tenía la actividad de un
 * panel de LEDs. Siete décimas de segundo es lo que tarda una luz en encenderse en una casa de
 * verdad, y ahora son también parte de por qué el conjunto va tranquilo: con el cambio apuntado
 * al ritmo de arriba, un encendido corto todavía se lee como un titileo, y uno largo se lee como
 * alguien entrando en una habitación.
 *
 * Se paga por ventana y solo mientras dura, no por ciudad: en cualquier momento hay una o dos en
 * el aire, y cada una son un par de `fillRect` en el lienzo de su edificio.
 */
export const WINDOW_FADE_MS = 700;

/**
 * The moon. Scenery: a banana passes in front of it, because it is painted as part
 * of the background and something that is not there must not stop a shot. Crossing
 * its face is still worth an achievement.
 *
 * Where it sits is drawn per round from the match seed, and some rounds have no
 * moon at all, so these are ranges rather than a position. src/moon.js owns the
 * arithmetic; nothing else should need to know how it moves.
 */
export const MOON = {
  /** How often a round opens with no moon at all. Roughly one in five. */
  absentChance: 0.2,
  /** How big it can be, in px. A smaller moon is a harder lucky shot. */
  minR: 32,
  maxR: 52,
  /**
   * Where its centre may sit, as fractions of the canvas.
   *
   * El suelo de la banda —0,19— está contado y no elegido: la torre más alta que la ciudad puede
   * levantar es una del centro, `TOWER_MAX` (520, ver city.js), así que el tejado más alto posible
   * está en y=200; 0,19 x 720 son 137, y con los 52 del radio mayor el disco llega a 189. Con el
   * 0,25 de cuando el mundo medía 540 el disco bajaba a 232 y se metía detrás de las torres
   * nuevas, que es justo lo que esta banda existe para evitar. Si algún día crecen esas torres,
   * este número crece con ellas: es la única atadura que tiene.
   */
  bandX: [0.09, 0.91],
  bandY: [0.11, 0.19],
  /** How fast it crosses the sky, in px per second. */
  driftPxPerSec: 3.5,
  /** One full turn of its face, in seconds. Slow enough to notice, not to distract. */
  spinSeconds: 240,
};

// --- passing sky -----------------------------------------------------------
/** Gap between shooting stars (ms), and how long each streak lives. */
export const METEOR_GAP_MIN_MS = 7000;
export const METEOR_GAP_MAX_MS = 22000;
export const METEOR_LIFE_MS = 750;
/** How far behind the head the tail is drawn, counted in frames of travel. */
export const METEOR_TAIL = 9;

/** The station crosses now and then — very occasionally, and very faintly. */
export const ISS_GAP_MIN_MS = 45000;
export const ISS_GAP_MAX_MS = 130000;
export const ISS_ALPHA = 0.24;

/** A rocket goes up now and then, and a booster comes back down later. */
export const ROCKET_GAP_MIN_MS = 35000;
export const ROCKET_GAP_MAX_MS = 95000;
export const ROCKET_PUFF_LIFE_MS = 1300;
export const ROCKET_PUFF_EVERY_MS = 55;

// --- the banner plane ------------------------------------------------------
/** A light aircraft towing a banner crosses the sky now and then. */
export const BANNER_GAP_MIN_MS = 40000;
export const BANNER_GAP_MAX_MS = 110000;/** Cruise speed in px per frame — slow enough to actually read the banner. */
export const BANNER_SPEED = 1.4;
export const BANNER_WIDTH = 150;
export const BANNER_HEIGHT = 18;
/** Distance from the aircraft to the middle of the banner. */
export const BANNER_TOW = 122;

/** What the banner says. Keep every line short enough to fit the banner. */
export const BANNER_COUNT = 11;

/**
 * Air Force One, with its escort. Higher and rarer than the banner plane,
 * because the point of it is that seeing it is an event.
 */
export const AIRFORCE_GAP_MIN_MS = 55000;
export const AIRFORCE_GAP_MAX_MS = 140000;
export const AIRFORCE_SPEED = 2;
/** Where the two fighters sit, measured back from the big one. */
export const AIRFORCE_ESCORT_SIDE = 30;
export const AIRFORCE_ESCORT_DROP = 7;

/**
 * The bat-signal: a lamp on a far roof, a bat held on the clouds, and somebody
 * small standing up there watching it. Rarer than everything else and longer on
 * screen, because the point of it is that it is a little scene: the lamp stutters
 * on, he turns up late, and then the light goes out and he drops behind the roof.
 */
export const BATSIGNAL_GAP_MIN_MS = 45000;
export const BATSIGNAL_GAP_MAX_MS = 120000;
export const BATSIGNAL_LIFE_MS = 12000;
/** Where the emblem sits, and half the width of the beam where it meets it. */
export const BATSIGNAL_Y = 118;
export const BATSIGNAL_SPREAD = 40;
export const BATSIGNAL_GLOW = 30;
/** How long after the lamp before he turns up, and how long he takes to leave. */
export const BATSIGNAL_HERO_DELAY_MS = 3000;
export const BATSIGNAL_HERO_EXIT_MS = 900;
/** How far he sinks. Enough to clear the roofline he is standing on. */
export const BATSIGNAL_HERO_DROP = 26;

// --- cpu opponent ----------------------------------------------------------

/** Finished matches kept in the local leaderboard. Older ones fall off the end. */
export const MAX_RESULTS = 60;

// --- the online leaderboard ------------------------------------------------
/**
 * Where the shared ranking lives. Relative on purpose: the Node server in
 * server/ serves both the game and the API, so on the real deploy this needs no
 * configuration at all. Point it at an absolute URL to host them apart.
 */
export const LEADERBOARD_URL = '/api/leaderboard';
/** Give up on the server after this, instead of leaving the panel spinning. */
export const LEADERBOARD_TIMEOUT_MS = 4000;

// --- random matches ---------------------------------------------------------

/**
 * The pool. Opening this stream is the act of joining it, and the server holds it open
 * until the match is over — which is what makes the join and the pipe the same request.
 */
export const POOL_URL = '/api/pool';
/** Where one shot is handed over. An opaque code; the server never reads it. */
export const POOL_SHOT_URL = '/api/pool/shot';
/** How many are waiting, so the button can say so before it is pressed. */
export const POOL_STATS_URL = '/api/pool/stats';

/**
 * How long to wait for a rival before handing the player a CPU instead.
 *
 * Long enough that a second player pressing the same button a few seconds later still
 * finds you, short enough that being alone with an empty pool is a blink rather than a
 * wait. The player can cut it short at any moment, and the panel says how long is left.
 */
export const POOL_WAIT_MS = 12000;
/** The countdown is asked for this often, not every frame. */
export const POOL_TICK_MS = 250;

// --- rooms ------------------------------------------------------------------
//
// A room is the pool with a name: the code is the room, so the same code is the door for as
// long as the room lives, and nobody has to hand a new one over between turns. Same pipe,
// same SSE, different question — "is he here yet" instead of "is anybody there".

/** Joining is opening this stream, and the seat is in the query. */
export const ROOM_URL = '/api/room';
/** Where one turn is handed over inside a room. An opaque code, relayed unread. */
export const ROOM_SHOT_URL = '/api/room/shot';
/**
 * Where a seat's token is kept, per code, so a reload is the same player in the same seat.
 * Only the code and the token: nothing about the match, which is in the match itself.
 */
export const ROOM_SEAT_KEY = 'qbasicgorillas/room/v1';

// --- characters ------------------------------------------------------------
//
// A character is a palette plus a SHAPE. The shape is the part that matters:
// drawGorilla() and hitsGorilla() both read it, so a wider animal is a wider
// target instead of a banana sailing through its shoulder. Every number here is
// in body-local pixels — x from the anchor, y from the anchor, y going down.

/** The classic. Every other species is written as a set of deltas from this. */
const BASE_SHAPE = {
  headY: -25,
  headRx: 14,
  headRy: 13,
  earDx: 11,
  earR: 4.5,
  earY: 1,
  muzzleY: 6,
  muzzleRx: 8,
  muzzleRy: 6,
  browY: 5,
  torsoHalf: 17,
  torsoTop: -13,
  torsoBottom: 14,
  torsoR: 10,
  bellyX: 9,
  bellyW: 18,
  bellyY: -4,
  bellyH: 16,
  bellyR: 7,
  legTopY: 6,
  legW: 12,
  legH: 15,
  legOffset: 15,
  legGap: 3,
  footY: 18,
  footW: 14,
  footH: 4,
  /** Hitbox only: the legs are narrower than the torso, and the feet lower. */
  legsHalf: 15.5,
  feetY: 22,
  shoulderDx: 12,
  shoulderY: -10,
  armW: 13,
  handR: 6,
  /** Where the two fists rest: the throwing one, and the hanging one. */
  fistDx: 30,
  fistDy: -6,
  freeDx: 26,
  freeDy: 14,
  shadowW: 38,
  shadowY: 20,
  /** Shaggy fur: a fringe of tufts around the head and shoulders. */
  shaggy: false,
  /**
   * Whatever grows out of the front of the face, as boxes in head-local coordinates:
   * the rhino's horn, the hippo's tusks. It was called `horn` while the rhino was the
   * only one that had anything, which stopped being true the moment a bird moved in.
   *
   * Painted AND tested — the same list both times — so a thing sticking out of a face
   * is a thing a banana can hit. That is the whole reason it is here rather than being
   * drawn as decoration. A face that is painted rather than grown — the duck's bill is
   * the orange patch of its muzzle — says so with `face` and `muzzle`, and has nothing
   * here: this list is for what leaves the head, not for what is drawn on it.
   */
  snout: null,
};

const shape = over => ({ ...BASE_SHAPE, ...over });

/**
 * Thirteen of them, so a duel can be a gibbon against a hippo. Skin colour lives
 * here too — there is no such thing as a separate "colour" choice.
 *
 * Everything a species is, it says with the pieces the hit test already reads: head,
 * ears, muzzle, torso, belly, legs, feet, arms, and the `snout` boxes. That is the
 * test for whether an animal belongs here. A character that needed drawing code of
 * its own could be somewhere other than where it can be hit, and in this game the
 * picture IS the target.
 */
export const SPECIES = [
  {
    id: 'gorilla',
    body: '#3f8f77', dark: '#1f4e42', belly: '#6fcbb0', face: '#6fcbb0',
    shape: shape({}),
  },
  {
    id: 'chimp',
    body: '#8a6a44', dark: '#4b3520', belly: '#d6b98c', face: '#e6cfa8',
    shape: shape({
      headRx: 11.5, headRy: 11, headY: -23,
      earDx: 10.5, earR: 6, earY: 2,
      muzzleRx: 7, muzzleRy: 5.5,
      torsoHalf: 14, torsoTop: -12, torsoBottom: 12,
      bellyX: 7, bellyW: 14, bellyH: 13,
      legsHalf: 13, legOffset: 12, legGap: 2, legH: 13,
      feetY: 20, footY: 16,
      shoulderDx: 10, armW: 11, handR: 5,
      shadowW: 32,
    }),
  },
  {
    id: 'orangutan',
    body: '#b4622c', dark: '#6d3413', belly: '#e0995f', face: '#8a4a20',
    shape: shape({
      headRx: 14.5, headRy: 14, headY: -27,
      earDx: 10, earR: 4,
      torsoHalf: 21, torsoTop: -15, torsoBottom: 15,
      bellyX: 11, bellyW: 22, bellyH: 18,
      legOffset: 17, legsHalf: 18,
      shoulderDx: 15, armW: 15,
      shadowW: 46,
      shaggy: true,
    }),
  },
  {
    id: 'baboon',
    body: '#9a7b52', dark: '#54402a', belly: '#e2c69b', face: '#7d5c3c',
    shape: shape({
      headY: -26, headRx: 13, headRy: 12,
      earDx: 11.5, earR: 3.5,
      muzzleY: 9, muzzleRx: 10, muzzleRy: 7,
      torsoHalf: 15.5, torsoTop: -14, torsoBottom: 14,
      legsHalf: 14, legH: 18, legOffset: 13, feetY: 26, footY: 22,
      armW: 11.5,
      shadowW: 34,
      shaggy: true,
    }),
  },
  {
    id: 'panda',
    body: '#e8e8ea', dark: '#1b1b20', belly: '#f6f6f8', face: '#f2f2f4',
    shape: shape({
      headRx: 15.5, headRy: 14,
      earDx: 12, earR: 6, earY: -6,
      muzzleRx: 7, muzzleRy: 5.5,
      torsoHalf: 19, torsoTop: -14, torsoBottom: 15,
      bellyX: 10, bellyW: 20, bellyH: 17,
      legOffset: 16, legsHalf: 17, legW: 14,
      footW: 15,
      shoulderDx: 13, armW: 14,
      shadowW: 44,
    }),
  },
  {
    id: 'bear',
    body: '#6f4a2a', dark: '#3c2715', belly: '#a97b4c', face: '#8a5c34',
    shape: shape({
      headY: -26, headRx: 15, headRy: 13.5,
      earDx: 12, earR: 5, earY: -8,
      muzzleY: 7, muzzleRx: 9, muzzleRy: 6.5,
      torsoHalf: 20, torsoTop: -14, torsoBottom: 14,
      bellyX: 11, bellyW: 22, bellyH: 16,
      legOffset: 15, legsHalf: 17, legW: 15, legH: 13,
      feetY: 20, footY: 16, footW: 16,
      shoulderDx: 14, armW: 15, handR: 7,
      shadowW: 46,
    }),
  },
  {
    id: 'rhino',
    body: '#7c8391', dark: '#41474f', belly: '#a8b0bd', face: '#9aa2ae',
    shape: shape({
      headY: -24, headRx: 17, headRy: 13,
      earDx: 12, earR: 3.5, earY: -9,
      muzzleY: 4, muzzleRx: 12, muzzleRy: 8,
      torsoHalf: 23, torsoTop: -15, torsoBottom: 15,
      bellyX: 13, bellyW: 26, bellyH: 16,
      legOffset: 16, legsHalf: 19, legW: 16, legH: 12,
      feetY: 20, footY: 16, footW: 18,
      shoulderDx: 16, armW: 17, handR: 8,
      shadowW: 52,
      // A horn is the clearest case of "the drawing is the hitbox": it sticks
      // out in front of the face, so it is a target, and it has to be in both
      // lists. Head-relative, like the accessories.
      //
      // Y va en el CENTRO de la cara, que no es donde nació: empezó pegado al borde
      // derecho de la cabeza (`x: 10` y `11` con la cabeza de radio 17), que es donde
      // lo pone medio mundo —el cuerno de perfil—, pero aquí el bicho se ve de frente y
      // un cuerno en una ceja se lee como un parche.
      //
      // Centrado hay que estrecharlo, y eso sólo se ve mirándolo: a 8 de ancho el cuerno
      // pasaba por encima de los DOS ojos y la cara quedaba vendada. A 6 cabe entre ellos
      // —que es donde mira quien lo dibuja de frente— y los deja a los lados. También
      // empieza más abajo (`y: -4` en vez de `-6`), para nacer del hocico (`muzzleY: 4`)
      // y no de la frente. La cabeza va de -37 a -11, así que el cuerno sigue naciendo y
      // muriendo dentro de ella: no asoma por encima, que es lo que hace el del perfil.
      //
      // Se mueve también en la hitbox, porque son los mismos números: el borde derecho
      // pierde los cuatro píxeles que le daba la punta y el centro de la cara los gana.
      // El `CRATE_PICKUP_W` (76) no se toca: sale del alcance más ancho con la mano, y
      // el rinoceronte sigue siendo el más ancho aunque pierda esos cuatro.
      snout: [
        { x: -3, y: -4, w: 6, h: 11, c: '#cfd5df' },
        { x: -2, y: -13, w: 4, h: 8, c: '#dfe4ec' },
      ],
    }),
  },

  // --- the wider family ------------------------------------------------------
  // Nothing below needs a new line of drawing code: these are the same pieces,
  // arranged differently, which is what makes them characters rather than pictures
  // pinned beside a hitbox.

  // An ape built like the chimp's slighter cousin: small head, no shag, a face pale
  // enough to be the first thing you notice about it.
  {
    id: 'bonobo',
    body: '#332c36', dark: '#191520', belly: '#cbb6a6', face: '#e6d2bd',
    shape: shape({
      headY: -24, headRx: 12, headRy: 11.5,
      earDx: 10, earR: 3.5, earY: 2,
      muzzleY: 6, muzzleRx: 7.5, muzzleRy: 5.5,
      torsoHalf: 14.5, torsoTop: -12, torsoBottom: 13,
      bellyX: 7, bellyW: 14, bellyH: 14,
      legOffset: 13, legGap: 2.5, legW: 11, legH: 13, legsHalf: 13.5,
      footY: 16, footW: 13, feetY: 20,
      shoulderDx: 10.5, armW: 11.5, handR: 5.5,
      freeDx: 24, freeDy: 16,
      shadowW: 32,
    }),
  },

  // The arms are the animal. A gibbon's reach is the one thing anybody knows about
  // it, so the hanging hand goes further out and further down than any other
  // species' — and because an arm is a stroke from the shoulder to that hand, the
  // hit test follows it all the way out there. Thin arms on a narrow body: it is the
  // lightest thing in the game and the hardest to hit, which is a real trade and not
  // a comment.
  {
    id: 'gibbon',
    body: '#2b2b31', dark: '#141318', belly: '#e9e3d5', face: '#f0eadc',
    shape: shape({
      headY: -24, headRx: 11, headRy: 10.5,
      earDx: 9, earR: 2.5, earY: 1,
      muzzleY: 5, muzzleRx: 6.5, muzzleRy: 5,
      torsoHalf: 13, torsoTop: -13, torsoBottom: 12,
      bellyX: 6, bellyW: 12, bellyH: 13,
      legOffset: 12, legGap: 2, legW: 10, legH: 14, legsHalf: 12.5,
      footY: 16, footW: 12, feetY: 20,
      shoulderDx: 9, shoulderY: -13, armW: 9.5, handR: 5,
      freeDx: 27, freeDy: 23,
      shadowW: 28,
    }),
  },

  // A baboon's build with its muzzle pushed out and painted: the face is the blue of a
  // mandrill's ridges, which no other species has, and the shag is its mane.
  {
    id: 'mandrill',
    body: '#8a7350', dark: '#493a22', belly: '#dbcba2', face: '#4f6fd6',
    shape: shape({
      headY: -27, headRx: 13.5, headRy: 12.5,
      earDx: 11, earR: 3, earY: 1,
      muzzleY: 8, muzzleRx: 11, muzzleRy: 7.5, browY: 7,
      torsoHalf: 16.5, torsoTop: -14, torsoBottom: 14,
      bellyX: 9, bellyW: 18, bellyH: 15,
      legOffset: 14, legsHalf: 15.5, legH: 15,
      shoulderDx: 12, armW: 12.5,
      shadowW: 36,
      shaggy: true,
    }),
  },

  // The heaviest animal in the game by a distance: the widest torso, the widest
  // shadow, the shortest legs and no neck worth the name. Two thirds of its face is
  // muzzle, and the tusks are a bar of tooth at the front of it.
  {
    id: 'hippo',
    body: '#8a80a0', dark: '#453f58', belly: '#c6bdd4', face: '#bdb2d2',
    shape: shape({
      headY: -23, headRx: 18, headRy: 13,
      earDx: 14, earR: 2.5, earY: -8,
      muzzleY: 6, muzzleRx: 16, muzzleRy: 11, browY: 3,
      torsoHalf: 24, torsoTop: -14, torsoBottom: 15, torsoR: 12,
      bellyX: 13, bellyW: 26, bellyH: 17,
      legOffset: 16, legGap: 3, legW: 17, legH: 14, legsHalf: 20,
      footY: 15, footH: 5, footW: 19, feetY: 20,
      shoulderDx: 17, armW: 17, handR: 7.5,
      shadowW: 56,
      // The tusks sit low on the muzzle, and that is not a detail: the snout boxes are
      // painted before the arms, so that a hand scratching its head goes over a hat —
      // and an animal this wide has a throwing arm that sweeps right across the front
      // of its own face. Measured on the first pass, the first set of tusks was drawn
      // and then covered by the arm that was already there. Down at the lip they are
      // below the sweep and in the clear.
      snout: [
        { x: 7, y: 13, w: 8, h: 3, c: '#f4f2e8' },
      ],
    }),
  },

  // A duck, in so far as a bird can be said with a head, a torso and two legs. The bill
  // is the muzzle: an orange patch low and wide on the face, which is the bill of every
  // duck in every cartoon. The ears are a fifth of a pixel of nothing: `earR: 0` would
  // draw a point, and a point is a speck of dust where a face should be.
  //
  // It carried two boxes in `snout` as well, and that was one bill too many — a patch
  // AND a bill, which is a duck with two mouths. At 1:1 nobody could tell; a head at 5x
  // is where it showed, and the patch is the one that stayed because it is the one that
  // reads as a bill from the side. The boxes were also the only part of a duck that
  // stuck out past its head, so they were extra hitbox: taking them out makes the bird
  // exactly what it looks like, which is the rule this whole file is written to.
  {
    id: 'duck',
    body: '#efe9d8', dark: '#b3aa94', belly: '#f8f4e8', face: '#e8a33c',
    shape: shape({
      headY: -24, headRx: 11.5, headRy: 12,
      earDx: 0, earR: 0.2,
      muzzleY: 6, muzzleRx: 10, muzzleRy: 4.5, browY: 5,
      torsoHalf: 15, torsoTop: -12, torsoBottom: 14,
      bellyX: 8, bellyW: 16, bellyH: 15,
      legOffset: 11, legGap: 3, legW: 9, legH: 13, legsHalf: 12,
      footY: 17, footH: 3, footW: 20, feetY: 20,
      shoulderDx: 10, armW: 10, handR: 5,
      shadowW: 34,
    }),
  },

  // No neck: the head is most of the animal and it sits straight on the shoulders,
  // which is the whole difference between a frog and every ape here. Wide feet for
  // the same reason a duck has them — they are the two animals that spend their time
  // in water and look it from the ankles up.
  {
    id: 'frog',
    body: '#4f9a3c', dark: '#2b5c22', belly: '#cfe8a8', face: '#a8dc72',
    shape: shape({
      headY: -22, headRx: 17, headRy: 12,
      earDx: 0, earR: 0.2,
      muzzleY: 8, muzzleRx: 15, muzzleRy: 8, browY: 3,
      torsoHalf: 19, torsoTop: -9, torsoBottom: 15,
      bellyX: 10, bellyW: 20, bellyH: 16,
      legOffset: 15, legGap: 4, legW: 13, legH: 11, legsHalf: 17,
      footY: 17, footH: 3, footW: 19, feetY: 20,
      shoulderDx: 13, armW: 11, handR: 5.5,
      shadowW: 42,
    }),
  },
];

// --- accessories -----------------------------------------------------------
//
// Each is a list of boxes, and that list is used TWICE: to paint the thing and
// to test hits against it. That is deliberate — an accessory cannot become a lie
// about where the character is, because there is only one description of it.
//
// Coordinates are relative to the HEAD, not the body, so a hat stays on when the
// head tilts, droops in a doze or snaps upright in a fright. Boxes are authored
// for a character facing right and mirrored for the other side.
//
// The upshot is honest and slightly funny: a top hat is a slightly larger head.
//
// All of it is free, on purpose. Half the wardrobe used to hang off an achievement — one
// hat per trophy — which made getting dressed up a reward for doing something else. This
// is a wardrobe, not a cabinet: every piece is there from the first round.

export const ACCESSORIES = [
  // --- hats (head-relative: y = 0 is the centre of the head, radius ~13) ---
  {
    id: 'cap', icon: '🧢', slot: 'hat',
    parts: [
      { x: -11, y: -21, w: 22, h: 8, c: '#cf4a4a' },
      { x: 6, y: -15, w: 15, h: 4, c: '#a83838' },
    ],
  },
  {
    id: 'beanie', icon: '🎿', slot: 'hat',
    parts: [
      { x: -11, y: -23, w: 22, h: 11, c: '#3f7fbf' },
      { x: -12, y: -14, w: 24, h: 4, c: '#2f6396' },
      { x: -2, y: -29, w: 5, h: 6, c: '#e8e8ea' },
    ],
  },
  {
    id: 'tophat', icon: '🎩', slot: 'hat',
    parts: [
      { x: -9, y: -37, w: 18, h: 18, c: '#1d1b22' },
      { x: -17, y: -21, w: 34, h: 5, c: '#1d1b22' },
      { x: -9, y: -21, w: 18, h: 4, c: '#8b2f45' },
    ],
  },
  {
    id: 'crown', icon: '👑', slot: 'hat',
    parts: [
      { x: -11, y: -22, w: 22, h: 6, c: '#d8a72a' },
      { x: -11, y: -30, w: 4, h: 8, c: '#d8a72a' },
      { x: -2, y: -32, w: 4, h: 10, c: '#d8a72a' },
      { x: 7, y: -30, w: 4, h: 8, c: '#d8a72a' },
      { x: -2, y: -25, w: 4, h: 4, c: '#e8534f' },
    ],
  },
  {
    id: 'viking', icon: '⛑️', slot: 'hat',
    parts: [
      { x: -11, y: -22, w: 22, h: 9, c: '#9aa2ae' },
      { x: -14, y: -30, w: 4, h: 7, c: '#e8e8ea' },
      { x: 10, y: -30, w: 4, h: 7, c: '#e8e8ea' },
      { x: -14, y: -23, w: 3, h: 9, c: '#e8e8ea' },
      { x: 11, y: -23, w: 3, h: 9, c: '#e8e8ea' },
    ],
  },
  {
    // Not a hat, but it goes where the hats go: a bow sits on the top of a head, and the
    // top of a head is what this slot is measured from. Two loops and a knot, in two pinks
    // so it has a fold rather than being a pink rectangle.
    //
    // It bites three pixels into the skull on purpose. A head is an ellipse, so its surface
    // at the width of a bow's loops is well below its top — measured, the first version had
    // the bow's lower edge three pixels clear of the head at the sides and it read as a bow
    // hovering over an animal. Overlap is what tied-on looks like, and the accessory is
    // painted after the head, so the overlap is invisible.
    id: 'bow', icon: '🎀', slot: 'hat',
    parts: [
      { x: -13, y: -19, w: 9, h: 7, c: '#f491bd' },
      { x: 4, y: -19, w: 9, h: 7, c: '#f491bd' },
      { x: -11, y: -14, w: 7, h: 5, c: '#ee7fae' },
      { x: 4, y: -14, w: 7, h: 5, c: '#ee7fae' },
      { x: -3, y: -17, w: 6, h: 6, c: '#d9538c' },
    ],
  },
  // --- face ---
  {
    id: 'shades', icon: '🕶️', slot: 'face',
    parts: [
      { x: -9, y: -3, w: 18, h: 4, c: '#15161c' },
    ],
  },
  {
    id: 'patch', icon: '🏴‍☠️', slot: 'face',
    parts: [
      { x: 2, y: -3, w: 7, h: 5, c: '#15161c' },
    ],
  },
  // --- neck (still head-relative: y ~ +11 is where the neck is) ---
  {
    id: 'scarf', icon: '🧣', slot: 'neck',
    parts: [
      { x: -12, y: 9, w: 24, h: 5, c: '#c8564f' },
      { x: 7, y: 13, w: 5, h: 10, c: '#a8443e' },
    ],
  },
  {
    id: 'bowtie', icon: '🎀', slot: 'neck',
    parts: [
      { x: -2, y: 11, w: 4, h: 4, c: '#1d1b22' },
      { x: -8, y: 10, w: 6, h: 6, c: '#8b2f45' },
      { x: 2, y: 10, w: 6, h: 6, c: '#8b2f45' },
    ],
  },
  {
    id: 'chain', icon: '📿', slot: 'neck',
    parts: [
      { x: -9, y: 10, w: 18, h: 3, c: '#d8a72a' },
      { x: -3, y: 13, w: 6, h: 5, c: '#f0c860' },
    ],
  },
];

/** The slots a character has, in the order the panel shows them. */
export const ACCESSORY_SLOTS = ['hat', 'face', 'neck'];

// --- game modes ------------------------------------------------------------
/**
 * Base numbers the modes override. A mode never invents its own tuning: it
 * points at one of these, so `config.js` stays the only place with magic
 * numbers in it even once there are eleven ways to play.
 */

/** Widest the wind ever blows, either direction. */
export const WIND_MAX = 14;
/** Low-gravity mode: about a third of earth's pull. */
export const GRAVITY_LUNAR = GRAVITY * 0.33;
/** Demolition mode: craters big enough to take a whole tower down. */
export const CRATER_SCALE_DEMOLITION = 3.2;
/** Demolition mode also throws a lot more of the building around. */
export const DEBRIS_SCALE_DEMOLITION = 2.4;

/** Storm mode: how often the wind changes its mind, and by how much. */
export const GUST_EVERY_MIN_MS = 1400;
export const GUST_EVERY_MAX_MS = 4000;
export const GUST_STRENGTH = 11;

/** Skyline heights, in canvas pixels. The far skyline is scenery: the game is played on the
 *  buildings in front of it. */
/**
 * Cuánto sube el skyline sobre el suelo, desde su base.
 *
 * Vive aquí y no en el cielo que lo pinta porque no es sólo suya: es la línea del horizonte, la
 * que decide dónde empieza la ciudad lejana y hasta dónde llegan las estrellas (ver city.js), y
 * dos módulos que no se conocen no pueden tener cada uno la mitad de la misma medida.
 */
export const SKYLINE_HEIGHT = 145;
export const SKYLINE_H_MIN = 50;
export const SKYLINE_H_MAX = 150;

/** Precipitation density, as particles across the whole sky. */
export const RAIN_DROPS = 150;
export const SNOW_FLAKES = 110;
/** Fall speed per frame. Snow drifts, rain does not. */
export const RAIN_SPEED = 11;
export const SNOW_SPEED = 1.4;
export const SNOW_DRIFT = 0.5;

// --- idle animation --------------------------------------------------------
/** Breaths per second for the gorillas' idle bob. */
export const BREATH_HZ = 0.5;
/** How long a blink lasts, and how often one comes around (ms). */
export const BLINK_MS = 130;
export const BLINK_PERIOD_MS = 3400;
/** How long the other side has to dither before the waiting gorilla nods off. */
export const SLEEP_AFTER_MS = 9000;
/** How long the jump when it is startled awake lasts. */
export const STARTLE_MS = 900;
